import React, { useEffect, useState } from 'react';
import {
  Card,
  Col,
  Row,
  Typography,
  FloatButton,
  Button,
  List,
  Space,
  Table,
  Statistic,
  Spin,
} from 'antd';
import { PlusOutlined, AppstoreOutlined, WarningOutlined, NumberOutlined } from '@ant-design/icons';
import AppLayout from '../../components/AppLayout';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const { Title, Text } = Typography;

interface Product {
  id: string;
  name: string;
  stock: number;
  unit: string;
  category: string;
}

const InventoryDashboard: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    axios.get(`${process.env.REACT_APP_API_URL}/inventory`)
      .then((res) => {
        setProducts(res.data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const totalQty = products.reduce((sum, p) => sum + Number(p.stock || 0), 0);
  const lowStock = products.filter((p) => p.stock <= 10);
  const lowStockPreview = lowStock.slice(0, 5);

  return (
    <AppLayout>
      <Spin spinning={loading}>
        <Title level={3}>Inventory Dashboard</Title>

        {/* Summary Cards */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card bordered={false} style={{ borderRadius: 12 }}>
              <Statistic
                title="Total Products"
                value={products.length}
                prefix={<AppstoreOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card bordered={false} style={{ borderRadius: 12 }}>
              <Statistic
                title="Low Stock Items"
                value={lowStock.length}
                prefix={<WarningOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card bordered={false} style={{ borderRadius: 12 }}>
              <Statistic
                title="Total Quantity in Stock"
                value={totalQty}
                prefix={<NumberOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Low Stock Preview */}
        <Card
          title="Low Stock Alerts"
          extra={
            <Button type="link" onClick={() => navigate('/inventory/products')}>
              View Full Inventory
            </Button>
          }
        >
          <Table
            dataSource={lowStockPreview}
            rowKey="id"
            size="small"
            pagination={false}
            columns={[
              {
                title: 'Product',
                dataIndex: 'name',
                key: 'name',
              },
              {
                title: 'Stock',
                dataIndex: 'stock',
                key: 'stock',
              },
              {
                title: 'Unit',
                dataIndex: 'unit',
                key: 'unit',
              },
              {
                title: 'Category',
                dataIndex: 'category',
                key: 'category',
                render: (text: string) => text || '—',
              },
            ]}
          />
        </Card>
      </Spin>
    </AppLayout>
  );
};

export default InventoryDashboard;
